import React from "react";
import SkeletonRows from "./Skeleton.jsx";
import { amount2, comparisonLabel, deltaParts } from "./derive.js";

/**
 * Service x account pairs ranked by the ABSOLUTE size of their change.
 *
 * Ranking by absolute rather than signed change is what puts the month's
 * biggest saving on the same list as its biggest increase — both are things
 * that moved, and a signed ranking buries every decrease below a tail of
 * trivial rises.
 *
 * While the month is in progress the comparison is the SAME days of last
 * month (`comparison`), never the whole of it — 12 days against 30 made every
 * row a fake drop and pushed real increases off the list.
 *
 * The compare toggle does not reach this section. Turning comparison off
 * quiets the row-level deltas in the panes below; a list whose entire purpose
 * is ranking by change has nothing left to show without them.
 */
export default function WhatMoved({ movers, prevPeriod, comparison, loading, selected, onSelect }) {
  const rows = (movers || []).map((m) => ({ ...m, delta: deltaParts(m.amount, m.previous_amount) })).filter((m) => m.delta);
  const maxAbs = rows.reduce((max, r) => Math.max(max, Math.abs(r.delta.diff)), 0) || 1;

  return (
    <section className="spend-section">
      <div className="section-head section-head-tight">
        <h2>What moved</h2>
        <span className="section-meta">Service × account, ranked by change vs {comparisonLabel(comparison, prevPeriod)}</span>
      </div>
      <p className="section-sub">Click a row to open it in the breakdown below.</p>

      <div className="mover-list">
        {loading && <SkeletonRows count={6} />}
        {/* "Nothing moved" is a finding, not a default — it must not be shown
            until the comparison has actually been made. */}
        {!loading && rows.length === 0 && (
          <div className="pane-empty">Nothing moved materially between these two months.</div>
        )}
        {!loading &&
          rows.map((m) => {
          const isSelected = selected.accountId === m.account_id && selected.service === m.service;
          const up = m.delta.direction === "up";
          return (
            <button
              key={`${m.account_id}|${m.service}`}
              type="button"
              className={`mover-row${isSelected ? " selected" : ""}`}
              aria-pressed={isSelected}
              onClick={() => onSelect(m.account_id, m.service)}
            >
              <span>
                <span className="mover-service">{m.service}</span>
                <span className="mover-account">{m.account_name}</span>
              </span>
              <span className="mover-track" aria-hidden="true">
                <span
                  className={`mover-fill${up ? "" : " mover-fill-down"}`}
                  style={{ width: `${(Math.abs(m.delta.diff) / maxAbs) * 100}%` }}
                />
              </span>
              <span className={`mover-delta${up ? " mover-delta-up" : " mover-delta-down"}`}>{m.delta.amountText}</span>
              <span className="mover-pct">{m.delta.pctLabel}</span>
              <span className="mover-amount">{amount2(m.amount)}</span>
            </button>
            );
          })}
      </div>
    </section>
  );
}
